import React, { useEffect, useMemo, useState } from 'react';
import Highlighter from 'react-highlight-words';
import {
  Bot,
  Code2,
  FileText,
  LayoutDashboard,
  Moon,
  Network,
  Phone,
  Plus,
  Search,
  Server,
  Settings,
  Shield,
  SlidersHorizontal,
  Sparkles,
  Sun,
} from 'lucide-react';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '../../components/ui';
import { useTheme } from '../../hooks/useTheme';
import { useAuthnMe } from '../../AuthnMeProvider';
import {
  REMOTE_DEV_BACKENDS,
  activeDevBackend,
  isDevBackendSwitchAvailable,
  switchDevBackend,
  type DevBackendId,
} from '../../devBackend/client';
import { usePreference } from '../../preferences';
import { useNavTree } from '../nav/useNavTree';
import type { NavNode } from '../nav/tree';
import { splitLocation } from '../nav/sidebarLevel';
import { navigate as routerNavigate, useUrl } from '../nav/useUrl';

type PaletteIcon = React.ComponentType<{ className?: string }>;
const HighlightWords = Highlighter as unknown as React.ComponentType<{
  autoEscape?: boolean;
  highlightClassName?: string;
  searchWords: string[];
  textToHighlight: string;
}>;

interface PaletteCommand {
  id: string;
  title: string;
  subtitle?: string;
  group: string;
  icon: PaletteIcon;
  shortcut?: string;
  search: string;
  run: () => void;
}

function breadcrumb(path: NavNode[]): string {
  return path
    .filter((node) => node.kind !== 'workspace')
    .map((node) => node.label)
    .join(' / ');
}

function iconForNode(node: NavNode): PaletteIcon {
  if (node.id === 'bot:dial') return Phone;
  if (node.kind === 'bot') return Bot;
  if (/(help|api-docs)$/.test(node.id)) return FileText;
  if (node.id === 'bot:settings' || node.id.startsWith('bot:')) return Settings;
  if (node.id.includes('workflow') || node.id === 'task-board') return Network;
  return LayoutDashboard;
}

function flattenNavCommands(
  node: NavNode,
  path: NavNode[],
  ctx: { botId?: string; customerId?: string },
  navigate: (url: string) => void,
): PaletteCommand[] {
  const nextPath = [...path, node];
  const commands: PaletteCommand[] = [];
  const url = node.to?.(ctx);
  if (url) {
    const crumb = breadcrumb(nextPath.slice(0, -1));
    const isBotSwitcher = node.kind === 'bot';
    const isSetting = node.id.startsWith('bot:') && nextPath.some((p) => p.id === 'bot:settings');
    commands.push({
      id: `nav:${node.id}`,
      title: isBotSwitcher ? `Open ${node.label}` : node.label,
      subtitle: crumb || node.desc,
      group: isBotSwitcher ? 'Bots' : isSetting ? 'Bot Settings' : 'Navigation',
      icon: iconForNode(node),
      search: [node.label, node.desc, crumb, node.id].filter(Boolean).join(' '),
      run: () => navigate(url),
    });
  }

  for (const child of node.children || []) {
    commands.push(...flattenNavCommands(child, nextPath, ctx, navigate));
  }
  return commands;
}

/** The schema playground (pages/SchemaBot.tsx): its settings toggle shows only there. */
const SCHEMA_BOT_PATH = /^\/b\/[^/]+\/schema-bot\/?$/;

function customerUrl(customerId: string | undefined, suffix = ''): string {
  return customerId ? `/customer/${customerId}${suffix}` : `/customer${suffix}`;
}

/** Localhost only: which backend this page talks to (src/devBackend/). */
function environmentCommands(runAndClose: (fn: () => void) => () => void): PaletteCommand[] {
  if (!isDevBackendSwitchAvailable()) return [];
  const current = activeDevBackend();
  const choices: Array<{ id: DevBackendId; label: string; subtitle: string; search: string }> = [
    {
      id: 'local',
      label: 'Local',
      subtitle: 'Use the local Go backend and reload.',
      search: 'local localhost go redis',
    },
    ...REMOTE_DEV_BACKENDS.map((backend) => {
      const host = new URL(backend.origin).host;
      return {
        id: backend.id,
        label: backend.label,
        subtitle: backend.id === 'prod'
          ? `Use ${host} as the backend and reload. Changes hit real customer data.`
          : `Use ${host} as the backend and reload.`,
        search: `${host} ${backend.id === 'prod' ? 'production live' : 'development staging'}`,
      };
    }),
  ];
  return choices.map((choice) => ({
    id: `environment-${choice.id}`,
    title: `Switch to ${choice.label} environment`,
    subtitle: choice.id === current ? 'Already active' : choice.subtitle,
    group: 'Environment',
    icon: Server,
    search: `environment env backend server api switch ${choice.id} ${choice.search}`,
    run: runAndClose(() => {
      if (choice.id !== current) switchDevBackend(choice.id);
    }),
  }));
}

function byGroup(commands: PaletteCommand[]): Array<[string, PaletteCommand[]]> {
  const order = ['Quick Actions', 'Bot Settings', 'Bots', 'Navigation', 'Workflows', 'Admin', 'Theme', 'Environment'];
  const grouped = new Map<string, PaletteCommand[]>();
  for (const command of commands) {
    const list = grouped.get(command.group) || [];
    list.push(command);
    grouped.set(command.group, list);
  }
  return Array.from(grouped.entries()).sort((a, b) => {
    const ai = order.indexOf(a[0]);
    const bi = order.indexOf(b[0]);
    return (ai === -1 ? order.length : ai) - (bi === -1 ? order.length : bi);
  });
}

function normalize(value: string): string {
  return value.toLowerCase().trim();
}

function scoreCommand(command: PaletteCommand, query: string): number {
  const tokens = normalize(query).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return 1;

  const title = normalize(command.title);
  const subtitle = normalize(command.subtitle || '');
  const group = normalize(command.group);
  const search = normalize(command.search);

  let score = 0;
  for (const token of tokens) {
    let tokenScore = 0;
    if (title === token) tokenScore = Math.max(tokenScore, 120);
    if (title.startsWith(token)) tokenScore = Math.max(tokenScore, 100);
    if (title.includes(` ${token}`)) tokenScore = Math.max(tokenScore, 90);
    if (title.includes(token)) tokenScore = Math.max(tokenScore, 80);
    if (subtitle.includes(token)) tokenScore = Math.max(tokenScore, 35);
    if (search.includes(token)) tokenScore = Math.max(tokenScore, 25);
    if (group.includes(token)) tokenScore = Math.max(tokenScore, 15);
    if (tokenScore === 0) return 0;
    score += tokenScore;
  }
  return score;
}

function queryTerms(query: string): string[] {
  return normalize(query).split(/\s+/).filter(Boolean);
}

function HighlightedText({ text, terms }: { text: string; terms: string[] }) {
  if (terms.length === 0) return <>{text}</>;
  return (
    <HighlightWords
      autoEscape
      highlightClassName="cmdk-highlight"
      searchWords={terms}
      textToHighlight={text}
    />
  );
}

export default function CommandPalette() {
  const nav = useNavTree();
  const theme = useTheme();
  const authn = useAuthnMe();
  const [isUseCodeEditor, setIsUseCodeEditor] = usePreference('isUseCodeEditor');
  const [openKbarUsingCmdShiftK, setOpenKbarUsingCmdShiftK] = usePreference('openKbarUsingCmdShiftK');
  const [animateOrb, setAnimateOrb] = usePreference('animateOrb');
  const [showSidekickOrb, setShowSidekickOrb] = usePreference('showSidekickOrb');
  const [showSchemaBotSettings, setShowSchemaBotSettings] = usePreference('showSchemaBotSettings');
  // Re-renders on navigation, so page-scoped commands follow the current page.
  const onSchemaBotPage = SCHEMA_BOT_PATH.test(splitLocation(useUrl()).pathname);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const roles = authn?.user?.roles || [];
  const isAuthenticated = !!authn?.isAuthenticated;
  const isAdmin = roles.includes('owner') || roles.includes('admin');
  const isReseller = roles.includes('reseller');

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.altKey) return;
      if (event.key.toLowerCase() !== 'k') return;
      if (event.shiftKey !== openKbarUsingCmdShiftK) return;
      event.preventDefault();
      setOpen((value) => !value);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openKbarUsingCmdShiftK]);

  const commands = useMemo(() => {
    const runAndClose = (fn: () => void) => () => {
      setOpen(false);
      fn();
    };
    const navigate = (url: string) => routerNavigate(url);
    const items: PaletteCommand[] = [];

    if (onSchemaBotPage) {
      // The preference store is shared: the open playground follows at once.
      items.push({
        id: 'toggle-schema-bot-settings',
        title: showSchemaBotSettings ? 'Hide schema playground settings' : 'Show schema playground settings',
        subtitle: showSchemaBotSettings
          ? 'Shown: pipeline, models, extract output, knowledge base and caller memory.'
          : 'Hidden: the playground shows only the call and its trace.',
        group: 'Quick Actions',
        icon: SlidersHorizontal,
        search: 'schema playground settings panel models knowledge base memory pipeline toggle preference',
        run: runAndClose(() => {
          // A failed save rolls the store back and keeps the error.
          setShowSchemaBotSettings(!showSchemaBotSettings).catch(() => {});
        }),
      });
    }

    if (isAuthenticated) {
      items.push({
        id: 'new-bot',
        title: 'Create a new bot',
        subtitle: 'Open the workspace bot creator.',
        group: 'Quick Actions',
        icon: Plus,
        search: 'new bot create assistant workspace',
        run: runAndClose(() => navigate(customerUrl(nav.customerId, '#new-bot'))),
      });

      if (nav.botId) {
        items.push({
          id: 'try-call',
          title: 'Try a call',
          subtitle: 'Open the public dial page for this bot.',
          group: 'Quick Actions',
          icon: Phone,
          search: 'try call dial public bot',
          run: runAndClose(() => navigate(`/b/${nav.botId}`)),
        });
      }

      items.push({
        id: 'toggle-code-editor-builder',
        title: isUseCodeEditor ? 'Turn off code-based workflow builder' : 'Use code-based workflow builder',
        subtitle: isUseCodeEditor
          ? 'Building new workflows with code; edits to an existing workflow always use the standard builder.'
          : 'Always using the standard builder.',
        group: 'Quick Actions',
        icon: Code2,
        search: 'code editor builder workflow conversation canvas graph preference toggle',
        run: runAndClose(() => {
          void setIsUseCodeEditor(!isUseCodeEditor);
        }),
      });

      items.push({
        id: 'toggle-open-kbar-using-cmd-shift-k',
        title: 'Toggle Open Kbar Using Cmd+Shift+K',
        subtitle: openKbarUsingCmdShiftK
          ? 'Enabled: Cmd+Shift+K opens this menu.'
          : 'Disabled: Cmd+K opens this menu.',
        group: 'Quick Actions',
        icon: Settings,
        search: 'command palette kbar keyboard shortcut cmd shift k preference toggle',
        run: runAndClose(() => {
          void setOpenKbarUsingCmdShiftK(!openKbarUsingCmdShiftK);
        }),
      });

      items.push(
        {
          id: 'workflows',
          title: 'Open workflows',
          subtitle: 'Automation workflows for this workspace.',
          group: 'Workflows',
          icon: Network,
          search: 'workflows automations list',
          run: runAndClose(() => navigate(customerUrl(nav.customerId, '/workflows'))),
        },
        {
          id: 'new-workflow',
          title: 'Create workflow',
          subtitle: 'Start a new automation workflow.',
          group: 'Workflows',
          icon: Plus,
          search: 'new workflow create automation',
          run: runAndClose(() => navigate(customerUrl(nav.customerId, '/workflows/new'))),
        },
        {
          id: 'task-board',
          title: 'Open task board',
          subtitle: 'Review scheduled and running workflow tasks.',
          group: 'Workflows',
          icon: LayoutDashboard,
          search: 'tasks task board scheduled running outbound',
          run: runAndClose(() => navigate(customerUrl(nav.customerId, '/tasks'))),
        },
      );
    }

    items.push(
      ...flattenNavCommands(
        nav.tree,
        [],
        { botId: nav.botId, customerId: nav.customerId },
        (url) => runAndClose(() => navigate(url))(),
      ),
    );

    if (isAdmin) {
      items.push({
        id: 'admin-console',
        title: 'Admin console',
        subtitle: 'Platform customers, bots, numbers, and system tools.',
        group: 'Admin',
        icon: Shield,
        search: 'admin console platform customers bots phone numbers',
        run: runAndClose(() => navigate('/admin')),
      });
    }

    if (isReseller) {
      items.push({
        id: 'reseller-dashboard',
        title: 'Reseller dashboard',
        subtitle: 'Manage reseller customers.',
        group: 'Admin',
        icon: Shield,
        search: 'reseller dashboard customers',
        run: runAndClose(() => navigate('/reseller')),
      });
    }

    items.push(
      {
        id: 'theme-light',
        title: 'Switch to light theme',
        subtitle: theme.resolvedTheme === 'light' ? 'Already active' : undefined,
        group: 'Theme',
        icon: Sun,
        search: 'theme light appearance',
        run: runAndClose(() => theme.setTheme('light')),
      },
      {
        id: 'theme-dark',
        title: 'Switch to dark theme',
        subtitle: theme.resolvedTheme === 'dark' ? 'Already active' : undefined,
        group: 'Theme',
        icon: Moon,
        search: 'theme dark appearance',
        run: runAndClose(() => theme.setTheme('dark')),
      },
      {
        id: 'toggle-animate-orb',
        title: animateOrb ? 'Turn off voice orb animation' : 'Turn on voice orb animation',
        subtitle: animateOrb
          ? 'Animated: the orb spins and reacts to voice (uses more GPU).'
          : 'Static: the orb paints a single frame.',
        group: 'Theme',
        icon: Sparkles,
        search: 'orb animation animate motion voice sphere sidekick gpu battery performance preference toggle',
        run: runAndClose(() => {
          void setAnimateOrb(!animateOrb);
        }),
      },
      {
        id: 'toggle-sidekick-orb',
        title: showSidekickOrb ? 'Hide the Sidekick orb' : 'Show the Sidekick orb',
        subtitle: showSidekickOrb
          ? 'Shown: the orb in the corner opens the Sidekick.'
          : 'Hidden: no orb in the corner (an open Sidekick panel stays).',
        group: 'Theme',
        icon: Sparkles,
        search: 'sidekick orb launcher hide show corner button assistant helper preference toggle',
        run: runAndClose(() => {
          void setShowSidekickOrb(!showSidekickOrb);
        }),
      },
    );

    items.push(...environmentCommands(runAndClose));

    const seen = new Set<string>();
    return items.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [
    animateOrb,
    isAdmin,
    isAuthenticated,
    isUseCodeEditor,
    isReseller,
    nav.botId,
    nav.customerId,
    nav.tree,
    onSchemaBotPage,
    openKbarUsingCmdShiftK,
    setAnimateOrb,
    setShowSidekickOrb,
    setIsUseCodeEditor,
    setOpenKbarUsingCmdShiftK,
    setShowSchemaBotSettings,
    showSchemaBotSettings,
    showSidekickOrb,
    theme,
  ]);

  const groups = useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed) return byGroup(commands);

    const scored = commands
      .map((command) => ({ command, score: scoreCommand(command, trimmed) }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.command.title.localeCompare(b.command.title);
      })
      .map((entry) => entry.command);

    return scored.length > 0 ? [['Best matches', scored] as [string, PaletteCommand[]]] : [];
  }, [commands, query]);

  const highlightTerms = useMemo(() => queryTerms(query), [query]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) setQuery('');
  };

  return (
    <CommandDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Command menu"
      commandProps={{ shouldFilter: false }}
    >
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder="Search pages and actions..."
      />
      <CommandList>
        {groups.length === 0 && <CommandEmpty>No matching page or action.</CommandEmpty>}
        {groups.map(([group, groupCommands], index) => (
          <React.Fragment key={group}>
            {index > 0 && <CommandSeparator />}
            <CommandGroup heading={group}>
              {groupCommands.map((command) => {
                const Icon = command.icon;
                return (
                  <CommandItem
                    key={command.id}
                    value={`${command.title} ${command.subtitle || ''} ${command.search}`}
                    onSelect={command.run}
                  >
                    <Icon className="cmdk-item-icon" />
                    <span className="cmdk-item-copy">
                      <span className="cmdk-item-title">
                        <HighlightedText text={command.title} terms={highlightTerms} />
                      </span>
                      {command.subtitle && (
                        <span className="cmdk-item-subtitle">
                          <HighlightedText text={command.subtitle} terms={highlightTerms} />
                        </span>
                      )}
                    </span>
                    {command.shortcut && <CommandShortcut>{command.shortcut}</CommandShortcut>}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </React.Fragment>
        ))}
      </CommandList>
      <div className="cmdk-footer">
        <span><Search className="cmdk-footer-icon" /> Search</span>
        <span>Enter to run</span>
        <span>Esc to close</span>
      </div>
    </CommandDialog>
  );
}
